import React from "react";

/** Parse inline markdown: **bold**, *italic*, [text](url), `code` */
const parseInline = (text) => {
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    // [text](url)
    const link = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    // **bold**
    const bold = remaining.match(/\*\*([^*]+)\*\*/);
    // `code`
    const code = remaining.match(/`([^`]+)`/);

    const candidates = [link, bold, code].filter(Boolean);
    if (candidates.length === 0) {
      parts.push(remaining);
      break;
    }
    const first = candidates.reduce((a, b) => (a.index < b.index ? a : b));
    if (first.index > 0) parts.push(remaining.slice(0, first.index));
    if (first === link) {
      const isInternal = first[2].startsWith("/");
      parts.push(
        <a
          key={key++}
          href={first[2]}
          {...(isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
          className="text-[#1088D4] font-medium underline decoration-[#1088D4]/30 underline-offset-2 hover:decoration-[#1088D4]"
        >
          {first[1]}
        </a>
      );
    } else if (first === bold) {
      parts.push(<strong key={key++} className="text-[#273B59] font-semibold">{first[1]}</strong>);
    } else if (first === code) {
      parts.push(<code key={key++} className="px-1.5 py-0.5 rounded bg-[#F7F9FC] text-[#1088D4] text-[0.92em] font-mono">{first[1]}</code>);
    }
    remaining = remaining.slice(first.index + first[0].length);
  }
  return parts;
};

/** Slugify heading text into a stable anchor id */
export const slugifyHeading = (s) =>
  s
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/** Extract list of H2 headings from body for TOC */
export const extractToc = (body) => {
  const headings = [];
  for (const line of body.split("\n")) {
    if (line.startsWith("## ")) {
      const text = line.slice(3).trim();
      headings.push({ text, id: slugifyHeading(text) });
    }
  }
  return headings;
};

/** Block-level renderers (kept small to reduce nesting in the main parser) */
const renderUl = (buffer, idx) => (
  <ul key={`ul-${idx}`} className="space-y-2 text-[#475569] text-[15px] pl-1">
    {buffer.map((b, i) => (
      <li key={`${idx}-${i}`} className="flex items-start gap-3">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1088D4] shrink-0"></span>
        <span className="leading-[1.75]">{parseInline(b)}</span>
      </li>
    ))}
  </ul>
);

const renderOl = (buffer, idx) => (
  <ol key={`ol-${idx}`} className="space-y-3 text-[#475569] text-[15px] counter-reset">
    {buffer.map((b, i) => (
      <li key={`${idx}-${i}`} className="flex items-start gap-3">
        <span className="shrink-0 w-7 h-7 rounded-full bg-[#EAF5FD] text-[#1088D4] font-bold text-[13px] flex items-center justify-center">{i + 1}</span>
        <span className="leading-[1.75] pt-0.5">{parseInline(b)}</span>
      </li>
    ))}
  </ol>
);

const renderBlockquote = (buffer, idx) => {
  const text = buffer.join(" ");
  const isTip = /^\*\*(TIP|PRO TIP|NOTE|WARNING|KEY TAKEAWAY):\*\*/.test(text);
  const label = isTip ? text.match(/^\*\*([^*]+):\*\*/)?.[1] : null;
  const content = isTip ? text.replace(/^\*\*[^*]+:\*\*\s*/, "") : text;
  return (
    <div key={`bq-${idx}`} className="my-2 rounded-xl border border-[#1088D4]/20 bg-gradient-to-br from-[#EAF5FD] to-white p-5">
      {label && (
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1088D4] mb-2">{label}</div>
      )}
      <div className="text-[#273B59] text-[15px] leading-[1.75]">{parseInline(content)}</div>
    </div>
  );
};

const renderTable = (buffer, idx) => {
  // buffer is array of row strings; first row = header, separator rows are skipped
  const rows = buffer.filter((r) => !/^\|[\s|:-]+\|$/.test(r));
  const parseRow = (r) => r.split("|").slice(1, -1).map((c) => c.trim());
  const header = parseRow(rows[0]);
  const data = rows.slice(1).map(parseRow);
  return (
    <div key={`tbl-${idx}`} className="my-2 overflow-x-auto rounded-xl border border-[#E2E8F0]">
      <table className="w-full text-left text-[14px]">
        <thead className="bg-[#273B59] text-white">
          <tr>
            {header.map((h, i) => (
              <th key={`h-${i}`} className="px-4 py-3 font-semibold whitespace-nowrap">{parseInline(h)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, r) => (
            <tr key={`r-${r}`} className={r % 2 === 0 ? "bg-white" : "bg-[#F7F9FC]"}>
              {row.map((c, i) => (
                <td key={`${r}-${i}`} className="px-4 py-3 border-t border-[#E2E8F0] text-[#273B59]">{parseInline(c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Mini markdown renderer that supports:
 *   ## Heading 2  / ### Heading 3
 *   - bullet  /  1. numbered
 *   > callout  /  > **TIP:** highlight
 *   | col | col |   pipe tables with header row + separator
 *   Inline: **bold**, [link](url), `code`
 */
export const renderBlogBody = (body) => {
  const lines = body.split("\n");
  const blocks = [];
  let buffer = [];
  let kind = null; // 'p', 'ul', 'ol', 'blockquote', 'table'

  const flush = () => {
    if (buffer.length === 0) return;
    const idx = blocks.length;
    if (kind === "p") {
      blocks.push(<p key={`p-${idx}`} className="text-[#475569] leading-[1.8] text-[15px]">{parseInline(buffer.join(" "))}</p>);
    } else if (kind === "ul") {
      blocks.push(renderUl(buffer, idx));
    } else if (kind === "ol") {
      blocks.push(renderOl(buffer, idx));
    } else if (kind === "blockquote") {
      blocks.push(renderBlockquote(buffer, idx));
    } else if (kind === "table") {
      blocks.push(renderTable(buffer, idx));
    }
    buffer = [];
    kind = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trimEnd();

    if (line.startsWith("## ")) {
      flush();
      const text = line.slice(3).trim();
      const id = slugifyHeading(text);
      blocks.push(
        <h2 key={`h2-${i}`} id={id} className="scroll-mt-24 mt-12 mb-5 text-2xl sm:text-3xl font-bold font-heading text-[#273B59]">
          {text}
        </h2>
      );
    } else if (line.startsWith("### ")) {
      flush();
      const text = line.slice(4).trim();
      blocks.push(
        <h3 key={`h3-${i}`} className="mt-8 mb-3 text-xl font-bold font-heading text-[#273B59]">
          {text}
        </h3>
      );
    } else if (line.startsWith("- ")) {
      if (kind !== "ul") flush();
      kind = "ul";
      buffer.push(line.slice(2));
    } else if (/^\d+\.\s/.test(line)) {
      if (kind !== "ol") flush();
      kind = "ol";
      buffer.push(line.replace(/^\d+\.\s/, ""));
    } else if (line.startsWith("> ")) {
      if (kind !== "blockquote") flush();
      kind = "blockquote";
      buffer.push(line.slice(2));
    } else if (line.startsWith("|") && line.endsWith("|")) {
      if (kind !== "table") flush();
      kind = "table";
      buffer.push(line);
    } else if (line.trim() === "") {
      flush();
    } else {
      if (kind !== "p") flush();
      kind = "p";
      buffer.push(line);
    }
  }
  flush();
  return blocks;
};
